import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

const SESSION_TOKEN_KEY = "ld_session_token";
const HEARTBEAT_INTERVAL = 3 * 60 * 1000; // 3 minutes
const STALE_THRESHOLD_MINUTES = 10;

function generateToken(): string {
  return crypto.randomUUID();
}

function getDeviceInfo(): string {
  const ua = navigator.userAgent;
  const isMobile = /Mobi|Android/i.test(ua);
  const isTablet = /Tablet|iPad/i.test(ua);
  const device = isTablet ? "Tablet" : isMobile ? "Mobile" : "Desktop";
  return `${device} – ${navigator.language}`;
}

export function useSessionGuard(user: User | null) {
  const [blocked, setBlocked] = useState(false);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tokenRef = useRef<string | null>(null);

  const cleanup = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  // Register or validate session
  useEffect(() => {
    if (!user) {
      cleanup();
      tokenRef.current = null;
      setBlocked(false);
      return;
    }

    let cancelled = false;

    const registerSession = async () => {
      const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
      await supabase.rpc("cleanup_stale_sessions");

      const { data: ownSession } = storedToken
        ? await supabase
            .from("active_sessions")
            .select("id")
            .eq("user_id", user.id)
            .eq("session_token", storedToken)
            .maybeSingle()
        : { data: null };

      if (ownSession) {
        // Resuming own session – just update heartbeat
        tokenRef.current = storedToken;
        if (!cancelled) setBlocked(false);
        startHeartbeat(user.id, storedToken!);
        return;
      }

      // Register this browser as the only active session, replacing stale/other device rows atomically.
      const newToken = generateToken();
      const { error } = await supabase.rpc("claim_active_session" as never, {
        _session_token: newToken,
        _device_info: getDeviceInfo(),
      } as never);

      if (error) {
        console.error("Failed to register session:", error);
        return;
      }

      localStorage.setItem(SESSION_TOKEN_KEY, newToken);
      tokenRef.current = newToken;
      if (!cancelled) setBlocked(false);
      startHeartbeat(user.id, newToken);
    };

    const startHeartbeat = (userId: string, token: string) => {
      cleanup();
      heartbeatRef.current = setInterval(async () => {
        const { error } = await supabase
          .from("active_sessions")
          .update({ last_active_at: new Date().toISOString() })
          .eq("user_id", userId)
          .eq("session_token", token);

        if (error) {
          console.error("Heartbeat failed:", error);
        }
      }, HEARTBEAT_INTERVAL);
    };

    registerSession();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [user?.id]);

  // On sign out, delete session
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event) => {
        if (event === "SIGNED_OUT") {
          const token = tokenRef.current || localStorage.getItem(SESSION_TOKEN_KEY);
          if (token) {
            await supabase
              .from("active_sessions")
              .delete()
              .eq("session_token", token);
          }
          localStorage.removeItem(SESSION_TOKEN_KEY);
          tokenRef.current = null;
          cleanup();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [cleanup]);

  return { blocked };
}
